///
/// EarthBrowser - GeoTIFF Handler
///
/// Handles GeoTIFF (.tif/.tiff) drag & drop: a worker decodes and
/// reprojects the raster to an equirectangular PNG + lat/lon bounds,
/// which then rides the standard image ground-overlay pipeline (window,
/// cloud save, shares, story tours). Ungeoreferenced TIFFs still open,
/// placed at the camera like a plain image.
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///

import { registerDatasetSourceFile } from '@/components/features/user-datasets/datasetSourceFiles'
import { toast } from '@/components/ui/use-toast'
import { isMobile } from '@/earthbrowser/utils/platform'
import { mountImageOverlay } from '@/utils/imageOverlayMount'
import type { GeotiffWorkerType } from '@/workers/geotiff/index'
import GeotiffWorker from '@/workers/geotiff/index.ts?worker'
import { wrap, type Remote } from 'comlink'
import { BaseFileHandler } from './BaseFileHandler'

export class GeoTiffFileHandler extends BaseFileHandler {
	private workerRef: Remote<GeotiffWorkerType> | null = null
	private worker: Worker | null = null

	canHandle(file: File): boolean {
		return /\.tiff?$/i.test(file.name)
	}

	initialize(): void {}

	private ensureWorker(): void {
		if (!this.worker) {
			this.worker = new GeotiffWorker()
			this.workerRef = wrap<GeotiffWorkerType>(this.worker)
		}
	}

	cleanup(): void {
		if (this.worker) {
			this.worker.terminate()
			this.worker = null
			this.workerRef = null
		}
	}

	async handleFile(file: File): Promise<string | null> {
		this.ensureWorker()
		if (!this.workerRef) {
			console.error('GeoTIFF worker could not be created')
			return null
		}

		try {
			const maxDim = isMobile() ? 2048 : 4096
			const result = await this.workerRef.convert(file, maxDim)
			for (const warning of result.warnings) {
				console.warn(`[geotiff] ${file.name}: ${warning}`)
			}

			// The converted PNG replaces the TIFF everywhere downstream:
			// the overlay mount, and the bytes parked for "Save to cloud"
			const pngName = file.name.replace(/\.tiff?$/i, '') + '.png'
			const pngFile = new File([result.png], pngName, { type: 'image/png' })
			const windowId = await mountImageOverlay(this.context.dispatch, this.context.getState, pngFile, {
				bounds: result.bounds ?? undefined,
			})
			if (windowId) {
				registerDatasetSourceFile(windowId, { file: pngFile, format: 'image' })
				if (!result.bounds) {
					toast({
						title: 'No georeference found',
						description: `${file.name} has no usable coordinate system — set its bounds in the overlay window.`,
						duration: 6000,
					})
				}
			}
			return windowId
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			console.error(`Failed to import GeoTIFF ${file.name}:`, error)
			toast({
				title: 'GeoTIFF import failed',
				description: message,
				duration: 8000,
			})
			return null
		}
	}
}
